// Color-scheme ("theme") support.
//
// A theme is a named set of primary + accent color scales declared as CSS
// variables in src/tailwind.css (`:root` = default, `:root[data-theme="x"]`
// = alternates). tailwind.config.js maps brand-*, accent-* and the stock
// blue-* utilities onto those variables, so switching `data-theme` on <html>
// re-colors the whole app without a re-render.
//
// Persistence:
//   - localStorage (`ff_theme`) so the choice survives reloads and is applied
//     before first paint by the inline script in public/index.html.
//   - Firestore users/{uid}/settings/ui { theme } so it follows a signed-in
//     user across devices. Cloud wins over local when both exist.

import { useCallback, useEffect, useState } from "react";
import { doc, getDoc, setDoc } from "firebase/firestore";
import { db } from "../firebase";

export const THEME_STORAGE_KEY = "ff_theme";
export const DEFAULT_THEME = "ocean";

// `primary` / `accent` are the representative swatch colors shown in the
// picker (500 / 600 of each scale). The real values live in tailwind.css.
export const THEMES = [
  { id: "ocean",  name: "Ocean",  description: "FlowFrame blue & crimson", primary: "#059dd9", accent: "#b71e4b" },
  { id: "rose",   name: "Rose",   description: "Pink & plum",              primary: "#ec4899", accent: "#9333ea" },
  { id: "forest", name: "Forest", description: "Green & amber",            primary: "#059669", accent: "#b45309" },
  { id: "plum",   name: "Plum",   description: "Violet & rose",            primary: "#8b5cf6", accent: "#e11d48" },
  { id: "sunset", name: "Sunset", description: "Orange & indigo",          primary: "#ea580c", accent: "#4f46e5" },
];

export const isValidTheme = (id) => THEMES.some((t) => t.id === id);

export function readStoredTheme() {
  try {
    const v = localStorage.getItem(THEME_STORAGE_KEY);
    return isValidTheme(v) ? v : DEFAULT_THEME;
  } catch {
    return DEFAULT_THEME;
  }
}

/** Apply a theme to the document and remember it locally. Returns the id applied. */
export function applyTheme(id) {
  const theme = isValidTheme(id) ? id : DEFAULT_THEME;
  const root = document.documentElement;
  root.setAttribute("data-theme", theme);

  // Keep the mobile browser chrome in step with the palette.
  const meta = document.querySelector('meta[name="theme-color"]');
  if (meta) {
    const c = getComputedStyle(root).getPropertyValue("--color-primary-700").trim();
    if (c) meta.setAttribute("content", c);
  }

  try {
    localStorage.setItem(THEME_STORAGE_KEY, theme);
  } catch {
    /* private mode / storage disabled — the in-memory choice still applies */
  }
  return theme;
}

const uiSettingsRef = (uid) => doc(db, "users", uid, "settings", "ui");

/**
 * React hook: current theme + setter. Pass the signed-in user (or null).
 * Mount it once, somewhere that is rendered on every tab.
 */
export function useTheme(currentUser) {
  const uid = currentUser?.uid || null;
  const [theme, setThemeState] = useState(readStoredTheme);

  useEffect(() => {
    applyTheme(theme);
  }, [theme]);

  // On sign-in, pull the cloud preference. If the account has none yet but
  // this device has a non-default choice, seed the cloud with it.
  useEffect(() => {
    if (!uid) return undefined;
    let cancelled = false;
    getDoc(uiSettingsRef(uid))
      .then((snap) => {
        if (cancelled) return;
        const cloud = snap.exists() ? snap.data()?.theme : null;
        if (isValidTheme(cloud)) {
          setThemeState(cloud);
        } else {
          const local = readStoredTheme();
          if (local !== DEFAULT_THEME) {
            setDoc(uiSettingsRef(uid), { theme: local, updatedAt: Date.now() }, { merge: true }).catch(() => {});
          }
        }
      })
      .catch((err) => console.warn("[theme] could not load cloud preference:", err?.message || err));
    return () => {
      cancelled = true;
    };
  }, [uid]);

  const setTheme = useCallback(
    (id) => {
      if (!isValidTheme(id)) return;
      setThemeState(id);
      if (uid) {
        setDoc(uiSettingsRef(uid), { theme: id, updatedAt: Date.now() }, { merge: true }).catch((err) =>
          console.warn("[theme] could not save preference:", err?.message || err)
        );
      }
    },
    [uid]
  );

  return { theme, setTheme, themes: THEMES };
}
